import React, { useState, useMemo, useRef, useEffect } from 'react';
import ListsSection from './ListsSection';
import CyclingTitle from './primitives/CyclingTitle';
import { TODAY, addDays, DUE_OPTIONS, DUE_TO_BUCKET, df } from '../utils/dates';
import { computeStreak, progressThisUnit } from '../utils/routines';
import SectionHeader from './primitives/SectionHeader';
import EmptyState    from './primitives/EmptyState';
import { useScrollLock } from '../hooks/useScrollLock';
import { Bell,
  Activity, Footprints, Dumbbell, HeartPulse, Bike, Waves,
  Brain, BookOpen, PenLine, Sparkles, Languages, Lightbulb,
  Pill, Droplets, Sun, Moon, Utensils, Wind,
  Music, Headphones, Code, Camera, Palette, Scroll,
  CircleDot, ChevronDown,
} from 'lucide-react';
import css from './TasksPage.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Icon system
// ─────────────────────────────────────────────────────────────────────────────

const ICON_MAP = {
  Activity, Footprints, Dumbbell, HeartPulse, Bike, Waves,
  Brain, BookOpen, PenLine, Sparkles, Languages, Lightbulb,
  Pill, Droplets, Sun, Moon, Utensils, Wind,
  Music, Headphones, Code, Camera, Palette, Scroll,
};

const ICON_CATEGORIES = [
  { label: 'body',        icons: ['Activity','Footprints','Dumbbell','HeartPulse','Bike','Waves'] },
  { label: 'mind',        icons: ['Brain','BookOpen','PenLine','Sparkles','Languages','Lightbulb'] },
  { label: 'maintenance', icons: ['Pill','Droplets','Sun','Moon','Utensils','Wind'] },
  { label: 'practice',    icons: ['Music','Headphones','Code','Camera','Palette','Scroll'] },
];

function renderRoutineIcon(iconName, routineName, size = 16) {
  if (iconName && ICON_MAP[iconName]) {
    const Icon = ICON_MAP[iconName];
    return <Icon size={size} strokeWidth={1.5} />;
  }
  return <CircleDot size={size} strokeWidth={1.5} style={{ color: 'var(--t3)' }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function relDue(dateStr) {
  if (!dateStr) return null;
  const d = df(dateStr);
  if (d < -1)   return `${Math.abs(d)}d overdue`;
  if (d === -1) return 'yesterday';
  if (d === 0)  return 'today';
  if (d === 1)  return 'tomorrow';
  if (d <= 6) {
    const days = ['sun','mon','tue','wed','thu','fri','sat'];
    return days[new Date(dateStr + 'T12:00:00').getDay()];
  }
  return `in ${d}d`;
}

function relReminder(isoStr) {
  if (!isoStr) return null;
  const r = new Date(isoStr);
  const now = new Date();
  const diffMs = r - now;
  const diffM  = Math.round(diffMs / 60000);
  if (diffM < 0)    return null; // past
  if (diffM < 60)   return `in ${diffM}m`;
  if (diffM < 1440) {
    const h = r.getHours();
    const m = r.getMinutes();
    const label = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m === 0 ? `${h12}${label}` : `${h12}:${String(m).padStart(2,'0')}${label}`;
  }
  const days = ['sun','mon','tue','wed','thu','fri','sat'];
  const h = r.getHours(), m = r.getMinutes();
  const label = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  const time = m === 0 ? `${h12}${label}` : `${h12}:${String(m).padStart(2,'0')}${label}`;
  return `${days[r.getDay()]} ${time}`;
}

function defaultReminderDatetime() {
  const d = new Date();
  d.setSeconds(0, 0);
  if (d.getHours() >= 9) {
    d.setDate(d.getDate() + 1);
  }
  d.setHours(9, 0, 0, 0);
  // Format as datetime-local value
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T09:00`;
}

function streakColorClass(n) {
  if (n >= 14) return css.streakHigh;
  if (n >= 7)  return css.streakMid;
  if (n >= 3)  return css.streakLow;
  return '';
}

function bucketDotClass(bucket, done) {
  if (done)                  return css.dotDone;
  if (bucket === 'today')    return css.dotToday;
  if (bucket === 'thisWeek') return css.dotThisWeek;
  if (bucket === 'later')    return css.dotLater;
  if (bucket === 'waiting')  return css.dotWaiting;
  return css.dotDefault;
}

const VIEW_FILTERS = ['unified', 'tasks', 'routines', 'lists'];

// ─────────────────────────────────────────────────────────────────────────────
// IconPicker
// ─────────────────────────────────────────────────────────────────────────────

function IconPicker({ selected, onSelect, onClear }) {
  return (
    <div className={css.iconPicker}>
      <div className={css.iconPickerCategory}>none</div>
      <div className={css.iconPickerGrid}>
        <button className={[css.iconTile, !selected ? css.iconTileSelected : ''].filter(Boolean).join(' ')}
          onClick={onClear} aria-label="No icon" type="button">
          <CircleDot size={18} strokeWidth={1.5} />
        </button>
      </div>
      {ICON_CATEGORIES.map(cat => (
        <div key={cat.label}>
          <div className={css.iconPickerCategory}>{cat.label}</div>
          <div className={css.iconPickerGrid}>
            {cat.icons.map(name => {
              const Icon = ICON_MAP[name];
              return (
                <button key={name}
                  className={[css.iconTile, selected === name ? css.iconTileSelected : ''].filter(Boolean).join(' ')}
                  onClick={() => onSelect(name)} aria-label={name} type="button">
                  <Icon size={18} strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button className={css.iconPickerFallback} onClick={onClear} type="button">
        use first letter instead
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IconPickerSheet — bottom sheet version of icon picker
// ─────────────────────────────────────────────────────────────────────────────

function IconPickerSheet({ selected, onSelect, onClose }) {
  useScrollLock(true);
  return (
    <div className={css.sheetOverlay} onClick={onClose}>
      <div className={css.sheet} onClick={e => e.stopPropagation()}>
        <div className={css.sheetHandle} />
        <div className={css.sheetHeader}>
          <span className={css.sheetTitle}>choose icon</span>
          <button className={css.sheetCancel} onClick={onClose} type="button">cancel</button>
        </div>

        {/* no icon option */}
        <button
          type="button"
          className={[css.noIconRow, !selected ? css.noIconRowActive : ''].filter(Boolean).join(' ')}
          onClick={() => onSelect('')}
        >
          <div className={css.noIconButton}>
            <CircleDot size={18} strokeWidth={1.5} color="var(--accent)" />
          </div>
          <div className={css.noIconText}>
            <div className={css.noIconLabel}>no icon</div>
            <div className={css.noIconHint}>use first letter of habit name</div>
          </div>
        </button>

        {/* icon categories */}
        {ICON_CATEGORIES.map(cat => (
          <div key={cat.label} className={css.iconCategory}>
            <div className={css.iconCategoryLabel}>{cat.label}</div>
            <div className={css.iconGrid}>
              {cat.icons.map(name => {
                const Icon = ICON_MAP[name];
                return (
                  <button key={name} type="button"
                    className={[css.iconCell, selected === name ? css.iconCellActive : ''].filter(Boolean).join(' ')}
                    onClick={() => onSelect(name)} aria-label={name}>
                    <Icon size={22} strokeWidth={1.5}
                      color={selected === name ? 'var(--accent)' : 'var(--ink)'} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <button className={css.sheetDone} onClick={onClose} type="button">done</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TaskRow
// ─────────────────────────────────────────────────────────────────────────────

function TaskRow({ task, lists, groupBy, toggleTask, setSt, notify, editTask, setEditTask, saveEditTask, deleteTask }) {
  const [rowExpanded, setRowExpanded] = React.useState(false);
  const isEditing   = editTask?.id === task.id;
  const isStaleTask = !task.done && task.created && (-df(task.created)) > 7;
  const dueStr      = task.dueDate ? relDue(task.dueDate) : null;
  const listName    = (lists||[]).find(l => l.id === task.listId)?.title || '';
  const isOverdue   = task.dueDate && df(task.dueDate) < 0 && !task.done;
  const reminderStr = task.reminderAt ? relReminder(task.reminderAt) : null;

  const metaParts = [];
  if (dueStr && !task.done)  metaParts.push({ text: dueStr, cls: isOverdue ? css.taskMetaOverdue : css.taskMetaItem });
  if (isStaleTask)           metaParts.push({ text: 'stale', cls: css.taskMetaOverdue });
  if (task.recurring && task.recurring !== 'none') metaParts.push({ text: `↻ ${task.recurring}`, cls: css.taskMetaRecurring });
  if (groupBy !== 'list' && listName) metaParts.push({ text: listName, cls: css.taskMetaItem });

  const DUE_CHIP_EDIT = [
    { id: 'today',    label: 'today' },
    { id: 'tomorrow', label: 'tomorrow' },
    { id: 'thisWeek', label: 'this week' },
    { id: 'custom',   label: 'custom' },
    { id: 'waiting',  label: 'waiting' },
  ];

  const openEdit = e => {
    e.stopPropagation();
    if (isEditing) { setEditTask(null); return; }
    setEditTask({
      id: task.id,
      text: task.text,
      dueDate: task.dueDate || '',
      recurring: task.recurring || 'none',
      dueOption: task.bucket === 'waiting' ? 'waiting'
               : task.bucket === 'thisWeek' ? 'thisWeek'
               : task.dueDate ? 'custom' : 'today',
      bucket: task.bucket || 'today',
      reminderAt: task.reminderAt || null,
    });
  };

  return (
    <div className={[css.taskRow, task.done ? css.taskRowDone : '', rowExpanded ? css.taskRowExpanded : ''].filter(Boolean).join(' ')} onClick={() => setRowExpanded(p=>!p)}>
      <div className={[css.taskBucketDot, bucketDotClass(task.bucket, task.done)].join(' ')} />

      <div className={css.taskBody} onClick={task.done ? () => toggleTask(task.id) : openEdit}>
        <div className={[css.taskText, task.done ? css.taskTextDone : ''].filter(Boolean).join(' ')}>{task.text}</div>
        {(metaParts.length > 0 || reminderStr) && (
          <div className={css.taskMeta}>
            {metaParts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className={css.taskMetaDot}>·</span>}
                <span className={p.cls}>{p.text}</span>
              </React.Fragment>
            ))}
            {reminderStr && (
              <>
                {metaParts.length > 0 && <span className={css.taskMetaDot}>·</span>}
                <span className={css.taskMetaReminder}>
                  <Bell size={11} strokeWidth={1.5} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
                  {reminderStr}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {task.priority === 'high' && !task.done && <span className={css.taskPriorityBang}>!</span>}

      <div className={css.taskActions}>
        <button className={[css.taskActionBtn, css.taskActionBtnDanger].join(' ')} onClick={e => { e.stopPropagation(); deleteTask(task.id); }} title="Delete" aria-label="Delete">×</button>
      </div>

      {!task.done && (
        <div className={css.actStrip}>
          <button className={css.actChip} onClick={e => { e.stopPropagation(); setSt(p => ({ ...p, tasks: p.tasks.map(x => x.id === task.id ? { ...x, done: true } : x) })); notify('Task removed'); }}>done</button>
          <button className={css.actChip} onClick={e => { e.stopPropagation(); setSt(p => ({ ...p, tasks: p.tasks.map(x => x.id === task.id ? { ...x, bucket: 'thisWeek' } : x) })); notify('Moved to this week'); }}>this week</button>
          <button className={css.actChip} onClick={e => { e.stopPropagation(); setSt(p => ({ ...p, tasks: p.tasks.map(x => x.id === task.id ? { ...x, bucket: 'later' } : x) })); notify('Moved to later'); }}>later</button>
          {task.bucket !== 'today' && <button className={css.actChip} onClick={e => { e.stopPropagation(); setSt(p => ({ ...p, tasks: p.tasks.map(x => x.id === task.id ? { ...x, bucket: 'today' } : x) })); notify('Moved to today'); }}>today</button>}
        </div>
      )}

      {isEditing && (
        <div className={css.inlineEdit} onClick={e => e.stopPropagation()}>
          <input className={css.editInput} value={editTask.text}
            onChange={e => setEditTask(p => ({ ...p, text: e.target.value }))}
            autoFocus autoComplete="off"
            onKeyDown={e => { if (e.key === 'Enter') saveEditTask(); if (e.key === 'Escape') setEditTask(null); }} />
          <div className={css.editChipsRow}>
            {DUE_CHIP_EDIT.map(opt => (
              <button key={opt.id}
                className={[css.dueChip, editTask.dueOption === opt.id ? css.dueChipActive : ''].filter(Boolean).join(' ')}
                onClick={() => setEditTask(p => ({ ...p, dueOption: opt.id }))}>
                {opt.label}
              </button>
            ))}
            {editTask.dueOption === 'custom' && (
              <input type="date" className={css.customDateInput} value={editTask.dueDate || ''}
                onChange={e => setEditTask(p => ({ ...p, dueDate: e.target.value }))} />
            )}
          </div>
          <div className={css.editFieldRow}>
            <div className={css.editField}>
              <span className={css.editLabel}>repeat</span>
              <select className={css.editSelect} value={editTask.recurring || 'none'} onChange={e => setEditTask(p => ({ ...p, recurring: e.target.value }))}>
                <option value="none">once</option>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="monthly">monthly</option>
              </select>
            </div>
          </div>
          <div className={css.editFieldRow}>
            <span className={css.editLabel}>reminder</span>
            <input type="datetime-local" className={css.editInput}
              value={editTask.reminderAt || ''}
              onChange={e => setEditTask(p => ({ ...p, reminderAt: e.target.value || null }))} />
          </div>
          <div className={css.editActions}>
            <button className={css.editSaveBtn} onClick={saveEditTask}>save</button>
            <button className={css.editCancelBtn} onClick={() => setEditTask(null)}>cancel</button>
            <button className={css.editDeleteBtn} onClick={() => { deleteTask(task.id); setEditTask(null); }}>delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RoutineRow
// ─────────────────────────────────────────────────────────────────────────────

function RoutineRow({ r, onCheckIn, onDelete, onEdit, isEditing, editRoutine, setEditRoutine, editRoutineIconPicker, setEditRoutineIconPicker, saveEditRoutine, deleteRoutine }) {
  const target = r.target || { count: 1, unit: 'day' };
  const streak = computeStreak(r);
  const progress = progressThisUnit(r);
  const done = progress >= target.count;
  const isSimpleDaily = target.unit === 'day' && target.count === 1;
  // Fix 8: streak unit matches target.unit
  const streakUnit = target.unit === 'week' ? 'w' : 'd';

  let metaText = '';
  if (isSimpleDaily) {
    metaText = done ? 'done today' : 'today';
  } else if (target.unit === 'day') {
    metaText = done ? 'done today' : `${progress} of ${target.count} today`;
  } else {
    metaText = done ? 'done this week' : `${progress} of ${target.count} this week`;
  }

  const handleCheckIn = e => { e.stopPropagation(); onCheckIn(r.id); };
  const handleDelete  = e => { e.stopPropagation(); onDelete(r.id); };

  // Fix 1+9: when editing, transform entire row into edit form — no duplication
  if (isEditing && editRoutine) {
    return (
      <div className={css.routineRow} style={{ flexDirection: 'column', alignItems: 'stretch', padding: 0 }}>
        <div className={css.inlineEdit} onClick={e => e.stopPropagation()}>
          <div className={css.routineFormRow}>
            <button type="button"
              className={[css.symbolTrigger, editRoutineIconPicker ? css.symbolTriggerOpen : ''].filter(Boolean).join(' ')}
              onClick={() => setEditRoutineIconPicker(p => !p)}>
              {/* Fix 2: CircleDot fallback, never a letter */}
              {editRoutine.emoji && ICON_MAP[editRoutine.emoji]
                ? renderRoutineIcon(editRoutine.emoji, editRoutine.name, 18)
                : <CircleDot size={18} strokeWidth={1.5} style={{ color: 'var(--t3)' }} />}
            </button>
            <input className={[css.routineNameInput, css.routineNameInputFlex].join(' ')}
              value={editRoutine.name}
              onChange={e => setEditRoutine(p => ({ ...p, name: e.target.value }))}
              autoFocus autoComplete="off"
              onKeyDown={e => { if (e.key === 'Enter') saveEditRoutine(); if (e.key === 'Escape') { setEditRoutine(null); setEditRoutineIconPicker(false); } }} />
          </div>
          {editRoutineIconPicker && (
            <IconPicker
              selected={editRoutine.emoji}
              onSelect={name => { setEditRoutine(p => ({ ...p, emoji: name })); setEditRoutineIconPicker(false); }}
              onClear={() => { setEditRoutine(p => ({ ...p, emoji: '' })); setEditRoutineIconPicker(false); }}
            />
          )}
          <div className={css.targetRow}>
            <span className={css.targetLabel}>target</span>
            <div className={css.targetInputs}>
              <input className={css.targetCountInput} type="number" min="1" max="14"
                value={editRoutine.targetCount}
                onChange={e => setEditRoutine(p => ({ ...p, targetCount: Math.max(1, parseInt(e.target.value) || 1) }))} />
              <span className={css.targetPer}>× per</span>
              <div className={css.targetUnitToggle}>
                {['day','week'].map(u => (
                  <button key={u} type="button"
                    className={[css.targetUnitSeg, editRoutine.targetUnit === u ? css.targetUnitSegActive : ''].filter(Boolean).join(' ')}
                    onClick={() => setEditRoutine(p => ({ ...p, targetUnit: u }))}>{u}</button>
                ))}
              </div>
            </div>
          </div>
          {/* Fix 6: save/cancel left, delete right */}
          <div className={css.editActions}>
            <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
              <button className={css.editSaveBtn} onClick={saveEditRoutine} disabled={!editRoutine.name.trim()}>save</button>
              <button className={css.editCancelBtn} onClick={() => { setEditRoutine(null); setEditRoutineIconPicker(false); }}>cancel</button>
            </div>
            <button className={css.editDeleteBtn} onClick={() => { deleteRoutine(r.id); setEditRoutine(null); }}>delete</button>
          </div>
        </div>
      </div>
    );
  }

  // Normal row display
  return (
    <div className={[css.routineRow, done ? css.routineRowDone : ''].filter(Boolean).join(' ')} onClick={onEdit} style={{ cursor: 'pointer' }}>
      <div className={css.streakPip}>
        {streak >= 3 ? (
          <><span className={streakColorClass(streak)} style={{ fontFamily: 'var(--sans)', fontSize: 'var(--fs-xs)' }}>{streak}</span><span className={css.streakDays}>{streakUnit === 'w' ? (streak===1?' wk':' wks') : (streak===1?' day':' days')}</span></>
        ) : streak >= 1 ? (
          <div className={css.streakPipDot} />
        ) : (
          <div className={css.streakPipEmpty} />
        )}
      </div>
      <div className={css.routineIcon}>{renderRoutineIcon(r.emoji, r.name, 16)}</div>
      <div className={css.routineBody}>
        <div className={[css.routineName, done ? css.routineNameDone : ''].filter(Boolean).join(' ')}>{r.name}</div>
        <div className={css.routineMeta}>{metaText}</div>
      </div>
      {isSimpleDaily ? (
        <button
          className={[css.checkInBtn, done ? css.checkInBtnDone : ''].filter(Boolean).join(' ')}
          onClick={handleCheckIn}
          aria-label={done ? `Undo ${r.name}` : `Check in ${r.name}`}
        >
          {done && (
            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
              <path d="M1 5l4 4 6-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      ) : (
        <button
          className={[css.progressPips, done ? css.progressPipsDone : ''].filter(Boolean).join(' ')}
          onClick={e => { if (!done) handleCheckIn(e); else e.stopPropagation(); }}
          aria-label={`${r.name}: ${progress}/${target.count}`}
        >
          {Array.from({ length: Math.min(target.count, 7) }, (_, i) => (
            <span key={i} className={[css.pip, i < progress ? css.pipFilled : css.pipEmpty].join(' ')} />
          ))}
        </button>
      )}
      <button className={css.routineDeleteBtn} onClick={handleDelete} aria-label="Delete habit">×</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TasksPage
// ─────────────────────────────────────────────────────────────────────────────

function TasksPage({ tasks=[], lists=[], routines=[], quickLists=[], toggleTask, setSt, notify }) {
  const [form, setForm] = useState({
    text: '', dueOption: 'today', customDate: '',
    recurring: 'none', mode: 'scheduled', reminderAt: null,
  });
  const [showReminder, setShowReminder]   = useState(false);
  const [showAddOpts,  setShowAddOpts]    = useState(false);
  const [viewFilter, setViewFilter]       = useState('unified');
  const [groupBy, setGroupBy]             = useState('bucket');
  const [showDone, setShowDone]           = useState(false);
  const [routineForm, setRoutineForm]     = useState({ name: '', emoji: '', targetCount: 1, targetUnit: 'day' });
  const [showRoutineAdd, setShowRoutineAdd] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [listForm, setListForm]           = useState({ name: '', emoji: '' });
  const [showListAdd, setShowListAdd]     = useState(false);
  const [editTask, setEditTask]           = useState(null);
  const [editRoutine, setEditRoutine]     = useState(null); // { id, name, emoji, targetCount, targetUnit }
  const [editRoutineIconPicker, setEditRoutineIconPicker] = useState(false);
  const [doneSectionOpen, setDoneSectionOpen] = useState(false);
  const addInputRef = useRef(null);

  // Reset form mode on mount
  useEffect(() => { setForm(p => ({ ...p, mode: 'scheduled', reminderAt: null })); }, []);

  // Close add forms when viewFilter changes
  useEffect(() => {
    setShowRoutineAdd(false);
    setShowIconPicker(false);
    setShowListAdd(false);
  }, [viewFilter]);

  // Part B migration: add target field to existing routines (runs once)
  useEffect(() => {
    const done = (() => { try { return localStorage.getItem('routines.migrated.targetField') === 'true'; } catch { return false; } })();
    if (done) return;
    const needs = (routines || []).some(r => !r.target);
    if (!needs) { try { localStorage.setItem('routines.migrated.targetField', 'true'); } catch {} return; }
    setSt(p => ({
      ...p,
      routines: (p.routines || []).map(r => r.target ? r : { ...r, target: { count: 1, unit: 'day' } }),
    }));
    try { localStorage.setItem('routines.migrated.targetField', 'true'); } catch {}
  }, []); // eslint-disable-line

  // ── Preserved logic ───────────────────────────────────────────────────────

  const deleteTask = id => {
    setSt(p => ({ ...p, tasks: (p.tasks||[]).filter(t => t.id !== id) }));
    notify('Task removed');
  };

  const saveEditTask = () => {
    if (!editTask || !editTask.text.trim()) return;
    // Compute bucket from dueOption
    let bucket = editTask.bucket || 'today';
    if (editTask.dueOption === 'waiting') bucket = 'waiting';
    else if (editTask.dueOption === 'today') bucket = 'today';
    else if (editTask.dueOption === 'tomorrow') bucket = 'today'; // lands in today+1
    else if (editTask.dueOption === 'thisWeek') bucket = 'thisWeek';
    else if (editTask.dueOption === 'custom') {
      if (!editTask.dueDate) bucket = 'later';
      else {
        const diff = Math.round((new Date(editTask.dueDate) - new Date(TODAY)) / 86400000);
        bucket = diff <= 0 ? 'today' : diff <= 7 ? 'thisWeek' : 'later';
      }
    }
    setSt(p => ({ ...p, tasks: (p.tasks||[]).map(t => t.id === editTask.id ? {
      ...t,
      text: editTask.text,
      dueDate: editTask.dueDate || t.dueDate,
      recurring: editTask.recurring,
      bucket,
      reminderAt: editTask.reminderAt ?? t.reminderAt,
    } : t) }));
    setEditTask(null);
    notify('Task updated');
  };

  const BUCKETS = [
    { id: 'today',    lbl: 'today' },
    { id: 'thisWeek', lbl: 'this week' },
    { id: 'later',    lbl: 'later' },
    { id: 'waiting',  lbl: 'waiting' },
  ];

  const isStale = t => !t.done && t.created && (-df(t.created)) > 7;

  const getEffectiveDueDate = () => {
    if (form.dueOption === 'custom') return form.customDate || TODAY;
    const opt = DUE_OPTIONS.find(o => o.id === form.dueOption) || DUE_OPTIONS[0];
    return opt.getDate();
  };

  const getEffectiveBucket = () => {
    if (form.mode === 'waiting') return 'waiting';
    if (form.dueOption === 'custom') {
      if (!form.customDate) return 'later';
      const diff = Math.round((new Date(form.customDate) - new Date(TODAY)) / 86400000);
      if (diff <= 0) return 'today';
      if (diff <= 7) return 'thisWeek';
      return 'later';
    }
    return DUE_TO_BUCKET[form.dueOption] || 'today';
  };

  const addTask = () => {
    if (!form.text.trim()) return;
    if (form.dueOption === 'custom' && !form.customDate) { notify('Pick a date first'); return; }
    const newTask = {
      id: `t${Date.now()}`, text: form.text,
      bucket: getEffectiveBucket(), dueDate: getEffectiveDueDate(),
      listId: 'l1', recurring: form.recurring || 'none',
      done: false, created: TODAY,
    };
    if (form.reminderAt) newTask.reminderAt = form.reminderAt;
    setSt(p => ({ ...p, tasks: [newTask, ...p.tasks] }));
    setForm({ text: '', dueOption: 'today', customDate: '', recurring: 'none', mode: 'scheduled', reminderAt: null });
    setShowReminder(false);
    notify('Task added');
  };

  const isDoneToday = r => r.lastDone === TODAY;

  const calcStreak = r => {
    if (!r.history || r.history.length === 0) return 0;
    const sorted = [...r.history].sort().reverse();
    let streak = 0;
    let check = new Date(TODAY);
    for (const d of sorted) {
      const expected = check.toISOString().slice(0, 10);
      if (d === expected) { streak++; check.setDate(check.getDate() - 1); }
      else if (streak === 0 && d === addDays(TODAY, -1)) { streak++; check.setDate(check.getDate() - 1); }
      else break;
    }
    return streak;
  };

  const checkInRoutine = id => {
    setSt(p => {
      const existing = (p.routines || []).find(r => r.id === id);
      if (!existing) return p;
      const target = existing.target || { count: 1, unit: 'day' };
      const nowISO = new Date().toISOString().slice(0, 16); // datetime for history
      const todayISO = TODAY;

      if (target.count === 1) {
        // Simple toggle: if already done today, undo
        const alreadyDone = (existing.history || []).some(h => h.slice(0, 10) === todayISO);
        if (alreadyDone) {
          const rs = (p.routines || []).map(r => r.id !== id ? r : {
            ...r, lastDone: '',
            history: (r.history || []).filter(h => h.slice(0, 10) !== todayISO),
          });
          notify(`${existing.name} unchecked`);
          return { ...p, routines: rs };
        }
        const rs = (p.routines || []).map(r => r.id !== id ? r : {
          ...r, lastDone: todayISO,
          history: [...(r.history || []), todayISO],
        });
        const updated = (rs||[]).find(x => x.id === id);
        const streak = updated ? computeStreak(updated) : 0;
        notify(streak >= 3 ? `${streak}-day streak on ${existing.name}` : `${existing.name} done`);
        return { ...p, routines: rs };
      }

      // Multi-count: append up to target, no undo
      const todayCount = (existing.history || []).filter(h => h.slice(0, 10) === todayISO).length;
      if (todayCount >= target.count) return p; // already at target
      const rs = (p.routines || []).map(r => r.id !== id ? r : {
        ...r,
        lastDone: todayISO,
        history: [...(r.history || []), nowISO],
      });
      const newCount = todayCount + 1;
      notify(`${existing.name}: ${newCount}/${target.count}`);
      return { ...p, routines: rs };
    });
  };

  const addRoutine = () => {
    if (!routineForm.name.trim()) return;
    const count = Math.max(1, parseInt(routineForm.targetCount) || 1);
    setSt(p => ({
      ...p,
      routines: [...(p.routines || []), {
        id: `r${Date.now()}`,
        name: routineForm.name.trim(),
        emoji: routineForm.emoji.trim() || '',
        target: { count, unit: routineForm.targetUnit || 'day' },
        streak: 0, lastDone: '', history: [],
      }],
    }));
    setRoutineForm({ name: '', emoji: '', targetCount: 1, targetUnit: 'day' });
    setShowRoutineAdd(false);
    setShowIconPicker(false);
    notify('Habit added');
  };

  const deleteRoutine = id => {
    setSt(p => ({ ...p, routines: (p.routines || []).filter(r => r.id !== id) }));
    notify('Habit removed');
  };

  const saveEditRoutine = () => {
    if (!editRoutine || !editRoutine.name.trim()) return;
    setSt(p => ({ ...p, routines: (p.routines || []).map(r => r.id !== editRoutine.id ? r : {
      ...r,
      name: editRoutine.name.trim(),
      emoji: editRoutine.emoji || '',
      target: { count: Math.max(1, parseInt(editRoutine.targetCount) || 1), unit: editRoutine.targetUnit || 'day' },
    }) }));
    setEditRoutine(null);
    setEditRoutineIconPicker(false);
    notify('Habit updated');
  };

  const openEditRoutine = r => {
    setEditRoutine({ id: r.id, name: r.name, emoji: r.emoji || '', targetCount: r.target?.count ?? 1, targetUnit: r.target?.unit ?? 'day' });
    setEditRoutineIconPicker(false);
    setEditTask(null);
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const pool = useMemo(() => (tasks||[]).filter(t => {
    if (!showDone && t.done) return false;
    return true;
  }), [tasks, showDone]);

  const buildGroups = () => {
    switch (groupBy) {
      case 'bucket':
        return BUCKETS.map(b => ({ ...b, tasks: pool.filter(t => t.bucket === b.id) })).filter(g => g.tasks.length > 0);
      case 'list': {
        const map = {};
        pool.forEach(t => { const n = (lists||[]).find(l => l.id === t.listId)?.title || 'unsorted'; if (!map[n]) map[n] = []; map[n].push(t); });
        return Object.entries(map).map(([lbl, ts]) => ({ id: lbl, lbl, tasks: ts }));
      }
      case 'due': {
        const today = new Date(TODAY);
        const gs = [
          { id: 'overdue', lbl: 'overdue',     tasks: [] },
          { id: 'today',   lbl: 'due today',   tasks: [] },
          { id: 'week',    lbl: 'this week',   tasks: [] },
          { id: 'later',   lbl: 'later',       tasks: [] },
          { id: 'none',    lbl: 'no due date', tasks: [] },
        ];
        pool.forEach(t => {
          if (!t.dueDate) { gs[4].tasks.push(t); return; }
          const diff = Math.round((new Date(t.dueDate) - today) / 86400000);
          if (diff < 0) gs[0].tasks.push(t);
          else if (diff === 0) gs[1].tasks.push(t);
          else if (diff <= 7) gs[2].tasks.push(t);
          else gs[3].tasks.push(t);
        });
        return gs.filter(g => g.tasks.length > 0);
      }
      case 'priority':
        return [
          { id: 'high',   lbl: 'high',   tasks: pool.filter(t => t.bucket === 'today' || isStale(t)) },
          { id: 'medium', lbl: 'medium', tasks: pool.filter(t => t.bucket === 'thisWeek' && !isStale(t)) },
          { id: 'low',    lbl: 'low',    tasks: pool.filter(t => (t.bucket === 'later' || t.bucket === 'waiting') && !isStale(t)) },
        ].filter(g => g.tasks.length > 0);
      default: return [];
    }
  };

  const groups          = useMemo(() => buildGroups(), [pool, groupBy]); // eslint-disable-line
  const openCount       = useMemo(() => tasks.filter(t => !t.done).length, [tasks]);
  const doneTodayCount  = useMemo(() => tasks.filter(t => t.done).length, [tasks]);
  const routineList     = routines || [];
  const routinesDoneToday = routineList.filter(r => isDoneToday(r)).length;

  const uToday    = useMemo(() => {
    const pending = tasks.filter(t => !t.done && (t.bucket === 'today' || t.dueDate === TODAY));
    const done    = tasks.filter(t =>  t.done && (t.bucket === 'today' || t.dueDate === TODAY));
    return [...pending, ...done];
  }, [tasks]);
  const uHabits   = useMemo(() => {
    const pending = routineList.filter(r => progressThisUnit(r) < (r.target?.count ?? 1));
    const done    = routineList.filter(r => progressThisUnit(r) >= (r.target?.count ?? 1));
    return [...pending, ...done];
  }, [routineList]);
  const uOverdue  = useMemo(() => tasks.filter(t => !t.done && t.dueDate && df(t.dueDate) < 0 && t.bucket !== 'waiting' && !uToday.find(x => x.id === t.id)), [tasks, uToday]);
  const uStale    = useMemo(() => tasks.filter(t => isStale(t) && !uToday.find(x => x.id === t.id) && !uOverdue.find(x => x.id === t.id)), [tasks, uToday, uOverdue]);
  const uThisWeek = useMemo(() => tasks.filter(t => !t.done && t.bucket === 'thisWeek'), [tasks]);
  const uLater    = useMemo(() => tasks.filter(t => !t.done && t.bucket === 'later'), [tasks]);
  const uWaiting  = useMemo(() => tasks.filter(t => !t.done && t.bucket === 'waiting'), [tasks]);
  const uDone     = useMemo(() => [...tasks.filter(t => t.done), ...routineList.filter(r => isDoneToday(r))], [tasks, routineList]);

  const GROUP_OPTIONS = [
    { id: 'bucket',   lbl: 'bucket' },
    { id: 'list',     lbl: 'list' },
    { id: 'due',      lbl: 'due' },
    { id: 'priority', lbl: 'priority' },
  ];

  const sharedRowProps = { lists, groupBy, toggleTask, setSt, notify, editTask, setEditTask, saveEditTask, deleteTask };

  const renderTaskList = arr => arr.length === 0 ? null : (
    <div className={css.taskList}>
      {arr.map(t => <TaskRow key={t.id} task={t} {...sharedRowProps} />)}
    </div>
  );

  const RoutineProgressBar = ({ done, total }) => {
    if (total === 0) return null;
    return (
      <div className={css.routineProgressWrap}>
        <span className={css.routineProgressLabel}>{done} of {total} done</span>
        <div className={css.routineProgressTrack}>
          <div className={css.routineProgressFill} style={{ width: `${(done / total) * 100}%` }} />
        </div>
      </div>
    );
  };

  // Title rendering
  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={css.page}>

      {/* ── Cycling title ── */}
      <CyclingTitle
        pageLabel="track"
        filters={VIEW_FILTERS}
        labels={['track', 'tasks', 'habits', 'lists']}
        value={viewFilter}
        onChange={setViewFilter}
      />

      {/* ── Stats row placeholder ── */}
      {(openCount > 0 || doneTodayCount > 0 || routineList.length > 0) && (
        <div className={css.statsRow}>
          {openCount > 0 && <span className={css.statPill}>{openCount} open</span>}
          {doneTodayCount > 0 && <span className={css.statPill}>{doneTodayCount} done today</span>}
          {routineList.length > 0 && <span className={css.statPill}>{routinesDoneToday}/{routineList.length} habits</span>}
        </div>
      )}

      {/* ── Contextual add card — one form per viewFilter ── */}
      {(viewFilter === 'unified' || viewFilter === 'tasks') && (
        <div className={css.addCard}>
          {/* Mode toggle */}
          <div className={css.modeToggle}>
            <button className={[css.modeSeg, form.mode === 'scheduled' ? css.modeSegActive : ''].filter(Boolean).join(' ')}
              onClick={() => setForm(p => ({ ...p, mode: 'scheduled' }))} type="button">scheduled</button>
            <button className={[css.modeSeg, form.mode === 'waiting' ? css.modeSegActive : ''].filter(Boolean).join(' ')}
              onClick={() => setForm(p => ({ ...p, mode: 'waiting' }))} type="button">waiting on</button>
          </div>
          <input
            ref={addInputRef}
            className={css.addInput}
            value={form.text}
            onChange={e => setForm(p => ({ ...p, text: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && addTask()}
            placeholder="what needs to get done?"
            autoComplete="off"
            aria-label="New task"
          />
          <div className={css.chipGrid2x2}>
            {[
              { id: 'today',    label: 'today' },
              { id: 'tomorrow', label: 'tomorrow' },
              { id: 'thisWeek', label: 'this week' },
              { id: 'custom',   label: 'custom' },
            ].map(opt => (
              <button key={opt.id}
                className={[css.dueChip, form.dueOption === opt.id ? css.dueChipActive : ''].filter(Boolean).join(' ')}
                onClick={() => setForm(p => ({ ...p, dueOption: opt.id, customDate: '' }))}
              >{opt.label}</button>
            ))}
          </div>
          {form.dueOption === 'custom' && (
            <input type="date" className={css.customDateInput} value={form.customDate} min={TODAY}
              onChange={e => setForm(p => ({ ...p, customDate: e.target.value }))}
              placeholder="choose a date"
              style={{ colorScheme:'dark' }} />
          )}
          {!showAddOpts ? (
            <button className={css.remindMeLink} onClick={() => setShowAddOpts(true)} type="button">
              + options
            </button>
          ) : (
            <>
              <div className={css.selectWrap}>
                <select className={css.inlineSelectFull} value={form.recurring}
                  onChange={e => setForm(p => ({ ...p, recurring: e.target.value }))} aria-label="Recurring">
                  <option value="none">once</option>
                  <option value="daily">daily</option>
                  <option value="weekly">weekly</option>
                  <option value="monthly">monthly</option>
                </select>
                <ChevronDown size={12} strokeWidth={1.5} className={css.selectChevron} />
              </div>
              {!showReminder ? (
                <button className={css.remindMeLink} onClick={() => { setShowReminder(true); setForm(p => ({ ...p, reminderAt: p.reminderAt || defaultReminderDatetime() })); }} type="button">
                  + remind me
                </button>
              ) : (
                <div className={css.remindMeRow}>
                  <input type="datetime-local" className={css.reminderInput} value={form.reminderAt || ''}
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={e => setForm(p => ({ ...p, reminderAt: e.target.value }))} />
                  <button className={css.remindMeClear} onClick={() => { setShowReminder(false); setForm(p => ({ ...p, reminderAt: null })); }} type="button" aria-label="Remove reminder">×</button>
                </div>
              )}
            </>
          )}
          <button
            className={[css.addSubmitFull, form.text.trim() ? css.addSubmitFullActive : ''].filter(Boolean).join(' ')}
            onClick={addTask} disabled={!form.text.trim()}
          >{form.mode === 'waiting' ? 'add waiting task' : 'add task'}</button>
        </div>
      )}

      {/* ── Routine add form (routines view only) ── */}
      {viewFilter === 'routines' && showRoutineAdd && (
        <div className={css.addCard}>
          <div className={css.routineFormRow}>
            <button type="button"
              className={[css.symbolTrigger, showIconPicker ? css.symbolTriggerOpen : ''].filter(Boolean).join(' ')}
              onClick={() => setShowIconPicker(p => !p)} aria-label="Choose icon"
            >
              {routineForm.emoji && ICON_MAP[routineForm.emoji]
                ? renderRoutineIcon(routineForm.emoji, routineForm.name, 18)
                : <span className={css.symbolTriggerPlaceholder}>{routineForm.name ? routineForm.name.charAt(0).toUpperCase() : '◎'}</span>
              }
            </button>
            <input className={[css.routineNameInput, css.routineNameInputFlex].join(' ')}
              value={routineForm.name}
              onChange={e => setRoutineForm(p => ({ ...p, name: e.target.value }))}
              placeholder="morning run, read 20 min…"
              onKeyDown={e => e.key === 'Enter' && addRoutine()} autoFocus autoComplete="off" />
          </div>
          {showIconPicker && (
            <IconPickerSheet
              selected={routineForm.emoji}
              onSelect={name => { setRoutineForm(p => ({ ...p, emoji: name })); setShowIconPicker(false); }}
              onClose={() => setShowIconPicker(false)}
            />
          )}
          {/* Frequency target */}
          <div className={css.targetRow}>
            <span className={css.targetLabel}>target</span>
            <div className={css.targetInputs}>
              <input
                className={css.targetCountInput}
                type="number" min="1" max="14"
                value={routineForm.targetCount}
                onChange={e => setRoutineForm(p => ({ ...p, targetCount: Math.max(1, parseInt(e.target.value) || 1) }))}
              />
              <span className={css.targetPer}>× per</span>
              <div className={css.targetUnitToggle}>
                {['day','week'].map(u => (
                  <button key={u} type="button"
                    className={[css.targetUnitSeg, routineForm.targetUnit === u ? css.targetUnitSegActive : ''].filter(Boolean).join(' ')}
                    onClick={() => setRoutineForm(p => ({ ...p, targetUnit: u }))}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            className={[css.addSubmitFull, routineForm.name.trim() ? css.addSubmitFullActive : ''].filter(Boolean).join(' ')}
            onClick={addRoutine} disabled={!routineForm.name.trim()}
          >add habit</button>
          <button className={css.remindMeLink} style={{ textAlign: 'center' }}
            onClick={() => { setShowRoutineAdd(false); setShowIconPicker(false); setRoutineForm({ name: '', emoji: '', targetCount: 1, targetUnit: 'day' }); }}>
            cancel
          </button>
        </div>
      )}

      {/* ── List add form (lists view only) ── */}
      {viewFilter === 'lists' && showListAdd && (
        <div className={css.addCard}>
          <div className={css.routineFormRow}>
            <span className={css.symbolTrigger} style={{ cursor: 'default', opacity: 0.4 }}>
              <CircleDot size={18} strokeWidth={1.5} />
            </span>
            <input className={[css.routineNameInput, css.routineNameInputFlex].join(' ')}
              value={listForm.name}
              onChange={e => setListForm(p => ({ ...p, name: e.target.value }))}
              placeholder="groceries, reading list…"
              onKeyDown={e => {
                if (e.key === 'Enter' && listForm.name.trim()) {
                  setSt(p => ({ ...p, lists: [...(p.lists || []), { id: `l${Date.now()}`, title: listForm.name.trim(), emoji: '', items: [] }] }));
                  setListForm({ name: '', emoji: '' });
                  setShowListAdd(false);
                  notify('List added');
                }
              }} autoFocus autoComplete="off" />
          </div>
          <button
            className={[css.addSubmitFull, listForm.name.trim() ? css.addSubmitFullActive : ''].filter(Boolean).join(' ')}
            onClick={() => {
              if (!listForm.name.trim()) return;
              setSt(p => ({ ...p, lists: [...(p.lists || []), { id: `l${Date.now()}`, title: listForm.name.trim(), emoji: '', items: [] }] }));
              setListForm({ name: '', emoji: '' });
              setShowListAdd(false);
              notify('List added');
            }}
            disabled={!listForm.name.trim()}
          >add list</button>
          <button className={css.remindMeLink} style={{ textAlign: 'center' }}
            onClick={() => { setShowListAdd(false); setListForm({ name: '', emoji: '' }); }}>
            cancel
          </button>
        </div>
      )}

      {/* ── Filters strip (tasks view only) ── */}
      {viewFilter === 'tasks' && (
        <div className={css.filtersStrip}>
          <span className={css.filterLabel}>group by</span>
          <div className={css.filterChips}>
            {GROUP_OPTIONS.map(g => (
              <button key={g.id} className={[css.filterChip, groupBy === g.id ? css.filterChipActive : ''].filter(Boolean).join(' ')} onClick={() => setGroupBy(g.id)}>{g.lbl}</button>
            ))}
          </div>
          <button className={[css.showDoneToggle, showDone ? css.showDoneToggleOn : ''].filter(Boolean).join(' ')} onClick={() => setShowDone(p => !p)}>
            {showDone ? 'hide done' : 'show done'}
          </button>
        </div>
      )}

      {/* ══ UNIFIED VIEW ══ */}
      {viewFilter === 'unified' && (() => {
        const allEmpty = uToday.length === 0 && uHabits.length === 0 && uOverdue.length === 0
          && uStale.length === 0 && uThisWeek.length === 0 && uLater.length === 0 && uWaiting.length === 0;

        if (allEmpty) return <EmptyState message="nothing today" cta={{ label:"add a task above", onClick:() => addInputRef.current?.focus() }} />;

        return (
          <>
            {uToday.length > 0 && (
              <div className={css.groupWrap}>
                <SectionHeader label="today" progress={String(uToday.length)} />
                {renderTaskList(uToday)}
              </div>
            )}
            {uHabits.length > 0 && (
              <div className={css.groupWrap}>
                <SectionHeader label="habits" progress={`${routinesDoneToday}/${routineList.length}`} />
                <div className={css.routineList}>
                  {uHabits.map(r => <RoutineRow key={r.id} r={r} onCheckIn={checkInRoutine} onDelete={deleteRoutine} onEdit={() => openEditRoutine(r)} />)}
                </div>
              </div>
            )}
            {uOverdue.length > 0 && (
              <div className={css.groupWrap}>
                <div className={css.groupHdr} style={{ cursor: 'default' }}>
                  <div className={[css.groupDot, css.dotOverdue].join(' ')} />
                  <span className={[css.groupName, css.groupNameOverdue].join(' ')}>overdue</span>
                  <span className={css.groupMeta}>{uOverdue.length}</span>
                </div>
                {renderTaskList(uOverdue)}
              </div>
            )}
            {uStale.length > 0 && (
              <div className={css.groupWrap}>
                <div className={css.groupHdr} style={{ cursor: 'default' }}>
                  <div className={[css.groupDot, css.dotLater].join(' ')} />
                  <span className={css.groupName}>stale</span>
                  <span className={css.groupMeta}>{uStale.length}</span>
                </div>
                {renderTaskList(uStale)}
              </div>
            )}
            {uThisWeek.length > 0 && (
              <div className={css.groupWrap}>
                <SectionHeader label="this week" progress={String(uThisWeek.length)} />
                {renderTaskList(uThisWeek)}
              </div>
            )}
            {uLater.length > 0 && (
              <div className={css.groupWrap}>
                <SectionHeader label="later" progress={String(uLater.length)} />
                {renderTaskList(uLater)}
              </div>
            )}
            {uWaiting.length > 0 && (
              <div className={css.groupWrap}>
                <SectionHeader label="waiting" progress={String(uWaiting.length)} />
                {renderTaskList(uWaiting)}
              </div>
            )}
          </>
        );
      })()}

      {/* ══ TASKS VIEW ══ */}
      {viewFilter === 'tasks' && (
        groups.length === 0 ? (
          <EmptyState message="all clear." />
        ) : groups.map(group => {
          const openInGroup = group.tasks.filter(t => !t.done).length;
          const doneInGroup = group.tasks.filter(t => t.done).length;
          const isOD = group.id === 'overdue';
          const isTD = group.id === 'today';
          return (
            <div key={group.id} className={css.groupWrap}>
              <div className={css.groupHdr} style={{ cursor: 'default' }}>
                <div className={[css.groupDot, isOD ? css.dotOverdue : isTD ? css.dotToday : group.id === 'thisWeek' || group.id === 'week' ? css.dotThisWeek : group.id === 'waiting' ? css.dotWaiting : css.dotDefault].join(' ')} />
                <span className={[css.groupName, isOD ? css.groupNameOverdue : isTD ? css.groupNameToday : ''].filter(Boolean).join(' ')}>{group.lbl}</span>
                <span className={css.groupMeta}>{openInGroup} open{doneInGroup > 0 ? ` · ${doneInGroup} done` : ''}</span>
              </div>
              <div className={css.taskList}>
                {group.tasks.map(t => <TaskRow key={t.id} task={t} {...sharedRowProps} />)}
              </div>
            </div>
          );
        })
      )}

      {/* ══ ROUTINES VIEW ══ */}
      {viewFilter === 'routines' && (
        <>
          {/* Fix 3: RoutineProgressBar removed — redundant with statPill */}

          {!showRoutineAdd && (
            <button className={css.addRoutineTrigger} onClick={() => setShowRoutineAdd(true)}>+ add habit</button>
          )}

          {routineList.length === 0 ? (
            <EmptyState message="no habits yet" cta={{ label:"+ add first habit", onClick:() => setShowRoutineAdd(true) }} />
          ) : (
            <>
              <SectionHeader label="habits" progress={`${routinesDoneToday}/${routineList.length}`} />
              <div className={css.routineList}>
                {/* Fix 1: edit form is inside RoutineRow (transforms the row) */}
                {(() => {
                  const pending = routineList.filter(r => progressThisUnit(r) < (r.target?.count ?? 1));
                  const done    = routineList.filter(r => progressThisUnit(r) >= (r.target?.count ?? 1));
                  return [...pending, ...done].map((r, i) => {
                    const isDoneRow = progressThisUnit(r) >= (r.target?.count ?? 1);
                    const isFirstDone = isDoneRow && i === pending.length;
                    return (
                      <div key={r.id} style={isFirstDone ? { borderTop: '1px solid var(--b1)' } : {}}>
                        <RoutineRow r={r}
                          onCheckIn={checkInRoutine} onDelete={deleteRoutine}
                          onEdit={() => openEditRoutine(r)}
                          isEditing={editRoutine?.id === r.id}
                          editRoutine={editRoutine}
                          setEditRoutine={setEditRoutine}
                          editRoutineIconPicker={editRoutineIconPicker}
                          setEditRoutineIconPicker={setEditRoutineIconPicker}
                          saveEditRoutine={saveEditRoutine}
                          deleteRoutine={deleteRoutine}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
              {/* Fix 7: compact Option A — tight rows, no card wrapping */}
              <div className={css.heatmapSection}>
                <SectionHeader label="last 7 days" />
                <div className={css.heatmapList}>
                  {routineList.map(r => {
                    const days = Array.from({ length: 7 }, (_, i) => addDays(TODAY, -(6 - i)));
                    const streak = computeStreak(r);
                    const unit = (r.target?.unit === 'week') ? 'w' : 'd'; // Fix 8
                    return (
                      <div key={r.id} className={css.heatmapRow}>
                        <div className={css.heatmapName}>{r.name}</div>
                        <div className={css.heatmapDots}>
                          {days.map(d => {
                            const hit = (r.history || []).some(h => h.slice(0, 10) === d);
                            return <div key={d} title={d} className={[css.heatmapDot, hit ? css.heatmapDotHit : css.heatmapDotMiss, d === TODAY ? css.heatmapDotToday : ''].filter(Boolean).join(' ')} />;
                          })}
                        </div>
                        <div className={css.heatmapStreak}>{streak}{unit}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ══ LISTS VIEW ══ */}
      {viewFilter === 'lists' && (
        <>
          {!showListAdd && (
            <button className={css.addRoutineTrigger} onClick={() => setShowListAdd(true)}>+ add list</button>
          )}
          <ListsSection quickLists={quickLists || []} setSt={setSt} notify={notify} />
        </>
      )}
    </div>
  );
}

export default TasksPage;
